import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Sidebar from '../Core/Sidebar';
import Navbar from '../Core/Navbar';
import { Search, Filter, Download, ChevronLeft, ChevronRight, DollarSign, Percent, Clock, TrendingUp, Zap, Activity } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import '../../Dashboard Style/WalletTransaction.css';
import { getBinaryIncomeApi } from '../../Services/Operations/ProductAPI'; // Import the binary income API

const BinaryIncome = ({ title, incomeType }) => {
  const authToken = useSelector((state) => state.user?.token);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [binaryIncomeData, setBinaryIncomeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [totalMatching, setTotalMatching] = useState(0);
  const [totalDistributed, setTotalDistributed] = useState(0);
  const [totalCapping, setTotalCapping] = useState(0);

      const [isMobile, setIsMobile] = useState(false);
  
    // Check screen size on mount and resize
    useEffect(() => {
      const checkMobile = () => {
        const mobile = window.innerWidth < 992;
        setIsMobile(mobile);
  
        // Auto-close sidebar on mobile by default
        if (mobile) {
          setSidebarOpen(true);
        } else {
          setSidebarOpen(true);
        }
      };
  
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);
  
  
  
    const closeSidebar = () => {
      if (isMobile) {
        setSidebarOpen(false);
      }
    };

  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  // Initialize sidebar functionality
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar');
    const sidebarBody = document.querySelector('.sidebar-body');
    
    if (sidebar) {
      if (sidebarOpen) {
        sidebar.classList.remove('close');
      } else {
        sidebar.classList.add('close');
      }
    }
    
    if (sidebarBody) {
      sidebarBody.style.height = 'calc(100vh - 70px)';
      sidebarBody.style.overflowY = 'auto';
    }

    return () => {
      // Cleanup
    };
  }, [sidebarOpen]);
  
  useEffect(() => {
    // Add custom styles for Binary Income page
    const style = document.createElement('style');
    style.innerHTML = `
      /* Custom scrollbar for sidebar */
      .sidebar-body {
        height: calc(100vh - 70px);
        overflow-y: auto;
      }
      
      .sidebar-body::-webkit-scrollbar {
        width: 6px;
      }
      
      .sidebar-body::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      
      /* Binary Income specific styles */
      .ratio-badge {
        background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
        color: white;
        padding: 6px 15px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 700;
        display: inline-block;
        letter-spacing: 1px;
      }
      
      .status-badge-get {
        background: linear-gradient(135deg, #4cd964 0%, #5ac8fa 100%);
        color: white;
        padding: 6px 15px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
      }
      
      .status-badge-pending {
        background: linear-gradient(135deg, #ff9500 0%, #ff5e3a 100%);
        color: white;
        padding: 6px 15px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
      }
      
      .volume-box-binary {
        background: rgba(255, 255, 255, 0.03);
        border-radius: 8px;
        padding: 12px;
        border-left: 3px solid;
        margin-bottom: 8px;
      }
      
      .left-volume-box {
        border-left-color: #2196f3;
      }
      
      .right-volume-box {
        border-left-color: #9c27b0;
      }
      
      .matching-volume-box {
        border-left-color: #4caf50;
      }
      
      .capping-box {
        border-left-color: #ff9800;
      }
      
      .distributed-box {
        border-left-color: #00bcd4;
      }
      
      .volume-label {
        font-size: 11px;
        color: #adb5bd;
        text-transform: uppercase;
        font-weight: 600;
        margin-bottom: 4px;
      }
      
      .volume-value {
        font-weight: 700;
        font-size: 16px;
      }
      
      .percentage-badge {
        background: rgba(33, 150, 243, 0.2);
        color: #2196f3;
        padding: 8px 15px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 700;
        text-align: center;
      }
      
      .direct-count {
        background: rgba(156, 39, 176, 0.2);
        color: #9c27b0;
        padding: 8px 15px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 700;
        text-align: center;
      }
      
      /* Table styles */
      .binary-income-table {
        background: rgba(30, 30, 40, 0.8);
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      }
      
      .binary-income-table thead th {
        background: linear-gradient(135deg, rgba(255, 107, 107, 0.2) 0%, rgba(238, 90, 82, 0.2) 100%);
        color: #e9ecef;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 13px;
        letter-spacing: 0.8px;
        padding: 18px 15px;
        border-bottom: 2px solid rgba(255, 107, 107, 0.4);
        position: relative;
      }
      
      .binary-income-table thead th:after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 100%;
        height: 2px;
        background: linear-gradient(90deg, #ff6b6b, #ee5a52);
      }
      
      .binary-income-table tbody td {
        padding: 16px 15px;
        vertical-align: middle;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        color: #e9ecef;
        background: rgba(40, 40, 50, 0.5);
        transition: all 0.3s ease;
      }
      
      .binary-income-table tbody tr:nth-child(even) td {
        background: rgba(35, 35, 45, 0.5);
      }
      
      .binary-income-table tbody tr:hover td {
        background: rgba(50, 50, 65, 0.7);
        transform: scale(1.005);
      }
      
      /* SL. NO. styling */
      .sl-no-circle-binary {
        background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
        color: white;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 800;
        font-size: 14px;
        box-shadow: 0 4px 8px rgba(255, 107, 107, 0.3);
      }
      
      /* Pagination styles */
      .pagination-container-binary {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        background: rgba(30, 30, 40, 0.8);
        border-radius: 10px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        margin-top: 20px;
      }
      
      .pagination-info-binary {
        color: #adb5bd;
        font-size: 14px;
        font-weight: 500;
      }
      
      .pagination-buttons-binary .btn {
        margin: 0 4px;
        min-width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        transition: all 0.3s ease;
      }
      
      .pagination-buttons-binary .btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      }
      
      .page-numbers-binary {
        display: flex;
        gap: 5px;
      }
      
      /* Summary cards for binary */
      .summary-card-binary {
        background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
        border-radius: 12px;
        padding: 25px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
      }
      
      .summary-card-binary:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 4px;
      }
      
      .summary-card-binary.matching-binary:before {
        background: linear-gradient(90deg, #4caf50, #8bc34a);
      }
      
      .summary-card-binary.distributed-binary:before {
        background: linear-gradient(90deg, #00bcd4, #03a9f4);
      }
      
      .summary-card-binary.capping-binary:before {
        background: linear-gradient(90deg, #ff9800, #ffc107);
      }
      
      .summary-card-binary:hover {
        transform: translateY(-8px);
        box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4);
      }
      
      /* Search box styles */
      .search-box-binary {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 10px;
        padding: 12px 20px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        transition: all 0.3s ease;
      }
      
      .search-box-binary:focus-within {
        border-color: #ff6b6b;
        box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.2);
      }
      
      .search-box-binary input {
        background: transparent;
        border: none;
        color: white;
        flex: 1;
        padding: 0 15px;
        outline: none;
        font-size: 14px;
      }
      
      .search-box-binary input::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }
      
      /* Icon containers */
      .icon-circle-binary {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 15px;
        font-size: 22px;
      }
      
      .icon-circle-binary.matching {
        background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(139, 195, 74, 0.2));
        color: #4caf50;
      }
      
      .icon-circle-binary.distributed {
        background: linear-gradient(135deg, rgba(0, 188, 212, 0.2), rgba(3, 169, 244, 0.2));
        color: #00bcd4;
      }
      
      .icon-circle-binary.capping {
        background: linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 193, 7, 0.2));
        color: #ff9800;
      }
      
      /* Text colors */
      .text-matching-binary { color: #4caf50 !important; }
      .text-distributed-binary { color: #00bcd4 !important; }
      .text-capping-binary { color: #ff9800 !important; }
      .text-binary-primary { color: #ff6b6b !important; }
      .text-binary-secondary { color: #2196f3 !important; }
    `;
    document.head.appendChild(style);
    
    // Fetch data on component mount
    fetchBinaryIncome();

    return () => {
      document.head.removeChild(style);
    };
  }, [currentPage, perPage]);

  const fetchBinaryIncome = async () => {
    try {
      setLoading(true);
      const result = await getBinaryIncomeApi(authToken, currentPage, perPage);
      
      if (result.status) {
        setBinaryIncomeData(result.data);
        setTotalPages(result.pagination.last_page || 1);
        
        // Calculate totals
        const matchingTotal = result.data.reduce((sum, item) => 
          sum + (parseFloat(item.matching) || 0), 0);
        const distributedTotal = result.data.reduce((sum, item) => 
          sum + (parseFloat(item.distribute_amount) || 0), 0);
        const cappingTotal = result.data.reduce((sum, item) => 
          sum + (parseFloat(item.capping) || 0), 0);
        
        setTotalMatching(matchingTotal);
        setTotalDistributed(distributedTotal);
        setTotalCapping(cappingTotal);
        
        // toast.success(result.message || 'Binary income loaded successfully');
      } else {
        toast.error(result.message || 'Failed to load binary income');
      }
    } catch (error) {
      console.error('Error fetching binary income:', error);
      toast.error('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled by filteredData memo
  };
  
  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    setCurrentPage(1);
    toast.info(`Date filter changed to ${filter === 'all' ? 'All Time' : filter}`);
  };
  
  const filteredData = useMemo(() => {
    let filtered = [...binaryIncomeData];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.ratio?.toLowerCase().includes(query) ||
        item.status?.toLowerCase().includes(query) ||
        item.matching?.toString().includes(query) ||
        item.distribute_amount?.toString().includes(query) ||
        item.created_on?.toLowerCase().includes(query)
      );
    }
    
    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.created_on);
        
        switch (dateFilter) {
          case 'today':
            return itemDate.toDateString() === now.toDateString();
          case 'week': {
            const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return itemDate >= oneWeekAgo;
          }
          case 'month': {
            const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
            return itemDate >= oneMonthAgo;
          }
          case 'year': {
            const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
            return itemDate >= oneYearAgo;
          }
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [binaryIncomeData, searchQuery, dateFilter]);
  
  const handleDownload = () => {
    if (filteredData.length === 0) {
      toast.warning('No data to export');
      return;
    }
    
    const exportData = filteredData.map((item, index) => ({
      'SL No': (currentPage - 1) * perPage + index + 1,
      'Ratio': item.ratio,
      'Percentage': `${item.percentage}%`,
      'Total Direct': item.total_direct,
      'Require Direct': item.require_direct,
      'Left Volume': `$${parseFloat(item.left_volume || 0).toFixed(2)}`,
      'Right Volume': `$${parseFloat(item.right_volume || 0).toFixed(2)}`,
      'Matching Volume': `$${parseFloat(item.matching || 0).toFixed(2)}`,
      'Capping': `$${parseFloat(item.capping || 0).toFixed(2)}`,
      'Distributed Amount': `$${parseFloat(item.distribute_amount || 0).toFixed(2)}`,
      'Status': item.status,
      'Created Date': item.created_on
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Binary Income');
    XLSX.writeFile(wb, `Binary_Income_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Binary income report exported successfully!');
  };
  
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pageNumbers.push(i);
        }
      }
    }
    
    return pageNumbers;
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    if (status === 'GET') {
      return 'status-badge-get';
    }
    return 'status-badge-pending';
  };

  return (
    <div className="main-wrapper dark-theme">
      {/* Sidebar */}
      <nav className={`sidebar ${sidebarOpen ? '' : 'close'}`}>
        <div className="sidebar-header">
          <a href="#" className="sidebar-brand">
            <i className="me-2" data-lucide="atom"></i>
            Willow <span>Quantum</span>
          </a>
          <div className="sidebar-toggler" onClick={toggleSidebar}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
       <Sidebar
                   isOpen={!sidebarOpen}
                   onClose={closeSidebar}
                 />
       
                 {/* Mobile backdrop */}
                 {isMobile && sidebarOpen && (
                   <div
                     className="sidebar-backdrop"
                     onClick={closeSidebar}
                   />
                 )}
      </nav>

      <div className="page-wrapper">
        <Navbar toggleSidebar={toggleSidebar}  isSidebarOpen={sidebarOpen}
            isMobile={isMobile}/>
        <div className="page-content p-4">
          {/* Page Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="text-white fw-bold mb-1">{title}</h3>
              <p className="text-muted mb-0">Track your binary matching income and volume details</p>
            </div>
            <div>
              <span className="badge bg-gradient-binary px-3 py-2">
                <TrendingUp size={16} className="me-2" />
                Binary Matching Income
              </span>
            </div>
          </div>
          
          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="summary-card-binary matching-binary">
                <div className="d-flex align-items-center">
                  <div className="icon-circle-binary matching">
                    <Activity size={24} />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Matching Volume</h6>
                    <h3 className="text-white mb-0 text-matching-binary">${totalMatching.toFixed(2)}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="summary-card-binary distributed-binary">
                <div className="d-flex align-items-center">
                  <div className="icon-circle-binary distributed">
                    <DollarSign size={24} />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Distributed</h6>
                    <h3 className="text-white mb-0 text-distributed-binary">${totalDistributed.toFixed(2)}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="summary-card-binary capping-binary">
                <div className="d-flex align-items-center">
                  <div className="icon-circle-binary capping">
                    <Zap size={24} />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Capping</h6>
                    <h3 className="text-white mb-0 text-capping-binary">${totalCapping.toFixed(2)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Search and Filter Section */}
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="d-flex gap-3">
                <form onSubmit={handleSearch} className="flex-grow-1">
                  <div className="search-box-binary">
                    <Search size={18} className="text-muted me-2" />
                    <input
                      type="text"
                      placeholder="Search by ratio, status, or amount..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </form>
                <div className="dropdown">
                  <button
                    className="btn btn-outline-light dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <Filter size={16} className="me-2" />
                    {dateFilter === 'all' ? 'All Time' : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)}
                  </button>
                  <div className="dropdown-menu">
                    <button 
                      className={`dropdown-item ${dateFilter === 'today' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('today')}
                    >
                      Today
                    </button>
                    <button 
                      className={`dropdown-item ${dateFilter === 'week' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('week')}
                    >
                      This Week
                    </button>
                    <button 
                      className={`dropdown-item ${dateFilter === 'month' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('month')}
                    >
                      This Month
                    </button>
                    <button 
                      className={`dropdown-item ${dateFilter === 'year' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('year')}
                    >
                      This Year
                    </button>
                    <div className="dropdown-divider"></div>
                    <button 
                      className={`dropdown-item ${dateFilter === 'all' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('all')}
                    >
                      All Time
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex justify-content-end gap-2">
                <button 
                  className="btn btn-outline-light"
                  onClick={handleDownload}
                  disabled={filteredData.length === 0}
                >
                  <Download size={16} className="me-2" />
                  Export ({filteredData.length})
                </button>
                <div className="dropdown">
                  <button
                    className="btn btn-outline-light dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    {perPage} per page
                  </button>
                  <div className="dropdown-menu">
                    {[10, 15, 25, 50, 100].map((num) => (
                      <button
                        key={num}
                        className={`dropdown-item ${perPage === num ? 'active' : ''}`}
                        onClick={() => {
                          setPerPage(num);
                          setCurrentPage(1);
                          toast.info(`Displaying ${num} records per page`);
                        }}
                      >
                        {num} per page
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Table Section */}
          <div className="binary-income-table">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-binary-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-white">Loading binary income data...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>SL. NO.</th>
                      <th>RATIO</th>
                      <th>PERCENTAGE</th>
                      <th>DIRECT TEAM</th>
                      <th>VOLUME DETAILS</th>
                      <th>MATCHING</th>
                      <th>CAPPING</th>
                      <th>DISTRIBUTED AMOUNT</th>
                      <th>STATUS</th>
                      <th>TIMESTAMP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center py-5">
                          <div className="text-muted">
                            <TrendingUp size={48} className="mb-3 opacity-50 text-binary-primary" />
                            <h5 className="text-white">No binary income found</h5>
                            <p className="text-muted mb-0">Binary income will appear when matching conditions are met</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((item, index) => (
                        <tr key={item.id} className="border-bottom border-dark">
                          {/* SL. NO. */}
                          <td className="align-middle">
                            <div className="d-flex align-items-center">
                              <div className="sl-no-circle-binary">
                                {(currentPage - 1) * perPage + index + 1}
                              </div>
                            </div>
                          </td>
                          
                          {/* RATIO */}
                          <td className="align-middle">
                            <span className="ratio-badge">
                              {item.ratio}
                            </span>
                          </td>
                          
                          {/* PERCENTAGE */}
                          <td className="align-middle">
                            <div className="percentage-badge">
                              {item.percentage}%
                            </div>
                          </td>
                          
                          {/* DIRECT TEAM */}
                          <td className="align-middle">
                            <div className="d-flex flex-column gap-2">
                              <div className="direct-count">
                                Direct: {item.total_direct}/{item.require_direct}
                              </div>
                              <div className="text-center text-muted small">
                                Required: {item.require_direct}
                              </div>
                            </div>
                          </td>
                          
                          {/* VOLUME DETAILS */}
                          <td className="align-middle">
                            <div className="d-flex flex-column gap-2">
                              <div className="volume-box-binary left-volume-box">
                                <div className="volume-label">Left Volume</div>
                                <div className="volume-value text-binary-secondary">
                                  ${parseFloat(item.left_volume || 0).toFixed(2)}
                                </div>
                              </div>
                              <div className="volume-box-binary right-volume-box">
                                <div className="volume-label">Right Volume</div>
                                <div className="volume-value text-binary-primary">
                                  ${parseFloat(item.right_volume || 0).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </td>
                          
                          {/* MATCHING */}
                          <td className="align-middle">
                            <div className="volume-box-binary matching-volume-box">
                              <div className="volume-label">Matching Volume</div>
                              <div className="volume-value text-matching-binary">
                                ${parseFloat(item.matching || 0).toFixed(2)}
                              </div>
                            </div>
                          </td>
                          
                          {/* CAPPING */}
                          <td className="align-middle">
                            <div className="volume-box-binary capping-box">
                              <div className="volume-label">Capping</div>
                              <div className="volume-value text-capping-binary">
                                ${parseFloat(item.capping || 0).toFixed(2)}
                              </div>
                            </div>
                          </td>
                          
                          {/* DISTRIBUTED AMOUNT */}
                          <td className="align-middle">
                            <div className="volume-box-binary distributed-box">
                              <div className="volume-label">Distributed Amount</div>
                              <div className="volume-value text-distributed-binary">
                                ${parseFloat(item.distribute_amount || 0).toFixed(2)}
                              </div>
                            </div>
                          </td>
                          
                          {/* STATUS */}
                          <td className="align-middle">
                            <div className="text-center">
                              <span className={getStatusBadge(item.status)}>
                                {item.status}
                              </span>
                            </div>
                          </td>
                          
                          {/* TIMESTAMP */}
                          <td className="align-middle">
                            <div className="bg-dark p-3 rounded-3">
                              <div className="text-white small">
                                <Clock size={14} className="me-2 text-muted" />
                                {formatDate(item.created_on)}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {!loading && filteredData.length > 0 && (
            <div className="pagination-container-binary">
              <div className="pagination-info-binary">
                Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, filteredData.length)} of {filteredData.length} entries
              </div>
              <div className="pagination-buttons-binary">
                <button
                  className="btn btn-outline-light"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                
                <div className="page-numbers-binary d-inline-block mx-2">
                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      className={`btn ${currentPage === pageNum ? 'btn-binary-primary' : 'btn-outline-light'}`}
                      onClick={() => handlePageClick(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ))}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="mx-2 text-muted">...</span>
                      <button
                        className="btn btn-outline-light"
                        onClick={() => handlePageClick(totalPages)}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>
                
                <button
                  className="btn btn-outline-light"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <footer className="footer d-flex flex-row align-items-center justify-content-center px-4 py-3 border-top small">
          <p className="text-secondary mb-1 mb-md-0">Copyright © 2025 Willow Quantum. All Right Reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default BinaryIncome;